import React, { useState, useEffect } from "react";
import { Container, Navbar, Nav, Button, Offcanvas } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { Box } from "lucide-react";
import companyimg from "../assets/company.png";
import {
  FaUser,
  FaSignOutAlt,
  FaStore,
  FaBuilding,
  FaUserPlus,
  FaShoppingCart,
  FaBars,
  FaChartBar,
  FaHome,
  FaBell,
} from "react-icons/fa";

const selectedBranchName = localStorage.getItem("selectedBranchName");

const XGolifeDashboardComponent = ({ children }) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    { path: "/XGOLifeDashboard", icon: <FaHome size={22} />, title: "Dashboard" },
    { path: "/ViewStore", icon: <FaStore size={22} />, title: "Store" },
    { path: "/CreateUser", icon: <FaUserPlus size={22} />, title: "Users" },
    { path: "/StoreInventory", icon: <Box size={22} />, title: "Inventory " },
    { path: "/Dispatch", icon: <FaStore size={22} />, title: "Dispatch" },
    { path: "/Report", icon: <FaChartBar size={22} />, title: "Reports" },
    { path: "/Sales", icon: <FaChartBar size={22} />, title: "Sales" },
    { path: "/SalesByBranch", icon: <FaChartBar size={22} />, title: "Sales By Branch" },
  ]

  return (
    <div className="dashboard-wrapper d-flex">
      {/* TopBar */}
      <Navbar 
        fixed="top" 
        className={`shadow-sm transition-all ${isScrolled ? 'navbar-scrolled' : ''}`}
        style={{ 
          background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
          transition: 'all 0.3s ease'
        }}
      >
        <Container fluid className="d-flex align-items-center">
          <Button
            variant="link"
            className="d-md-none text-dark border-0 me-2"
            onClick={() => setShowSidebar(true)}
          >
            <FaBars size={24} />
          </Button>
          
          <div className="d-flex align-items-center">
            <img
              src={companyimg}
              alt="Company Logo"
              className="company-logo me-2"
              style={{ height: '45px', width: '45px', objectFit: 'contain' }}
            />
            <Navbar.Brand className="fw-bold text-dark">
              {selectedBranchName} <span className="brand-subtitle">( XGoLife )</span>
            </Navbar.Brand>
          </div>

          <Nav className="ms-auto d-flex align-items-center">
            <div className="notification-badge me-3 position-relative">
              <FaBell size={20} className="text-dark" />
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                3
              </span>
            </div>
            
            <Nav.Link className="d-none d-sm-flex align-items-center text-dark me-3">
              <FaUser className="me-2" /> Profile
            </Nav.Link>
            
            <Nav.Link className="d-none d-sm-flex align-items-center text-dark me-3">
              <FaSignOutAlt className="me-2" /> Logout
            </Nav.Link>
            
            <Link to="/XGoSwitchView">
              <Button
                variant="outline-dark"
                className="switch-view-btn"
                style={{ borderRadius: '20px', marginRight: 40 }}
              >
                Switch View
              </Button>
            </Link>
          </Nav>
        </Container>
      </Navbar>

      {/* Sidebar - Desktop */}
      <div
        className="sidebar d-none d-md-flex flex-column"
        style={{
          backgroundColor: '#2C3E50',
          minHeight: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          paddingTop: '80px',
          boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
          width: '350px' 
        }}
      >
        <Nav className="flex-column w-100">
          {menuItems.map((item) => (
            <Nav.Link
              key={item.path}
              as={Link}
              to={item.path}
              className={`
                menu-item mb-2 mx-3 px-3 py-2 rounded-3 d-flex align-items-center
                ${location.pathname === item.path ? 'active-menu-item' : ''}
              `}
              style={{
                color: location.pathname === item.path ? '#FFD700' : '#fff',
                backgroundColor: location.pathname === item.path ? 'rgba(255,215,0,0.1)' : 'transparent',
                transition: 'all 0.3s ease'
              }}
            >
              <span className="me-3">{item.icon}</span>
              <span className="menu-title">{item.title}</span>
            </Nav.Link>
          ))}
        </Nav>
      </div>

      {/* Sidebar - Mobile */}
      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="start"
        className="mobile-sidebar"
        style={{ backgroundColor: '#2C3E50' }}
      >
        <Offcanvas.Header closeButton closeVariant="white">
          <Offcanvas.Title className="text-white">Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column">
            {menuItems.map((item) => (
              <Nav.Link
                key={item.path}
                as={Link}
                to={item.path}
                className={`
                  mobile-menu-item mb-2 px-3 py-2 rounded-3
                  ${location.pathname === item.path ? 'active-mobile-item' : ''}
                `}
                onClick={() => setShowSidebar(false)}
                style={{
                  color: location.pathname === item.path ? '#FFD700' : '#fff',
                  backgroundColor: location.pathname === item.path ? 'rgba(255,215,0,0.1)' : 'transparent'
                }}
              >
                <span className="me-3">{item.icon}</span>
                {item.title}
              </Nav.Link>
            ))}
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content */}
      <div
        className="main-content"
        style={{
          marginTop: '40px',
          transition: 'all 0.3s ease',
          backgroundColor: '#fff',
          flexGrow: 1 
        }}
      >
        <Container fluid>{children}</Container>
      </div>

      <style>
        {`
          @media (max-width: 768px) {
            .main-content {
              margin-left: 0; 
            }
            .company-logo {
              height: 40px !important;
              width: 40px !important;
            }
            .brand-subtitle {
              font-size: 0.9rem;
            }
          }
          
          .menu-item:hover {
            background-color: rgba(255,215,0,0.15) !important;
            color: #FFD700 !important;
          }
          
          .navbar-scrolled {
            padding: 5px 0;
            background: rgba(255, 215, 0, 0.95) !important;
            backdrop-filter: blur(10px);
          }
          
          .notification-badge:hover {
            transform: scale(1.1);
            transition: transform 0.2s ease;
          }
          
          .switch-view-btn:hover {
            background-color: #FFD700;
            border-color: #FFD700;
            color: #2C3E50;
          }
          
          .mobile-menu-item:hover {
            background-color: rgba(255,215,0,0.15);
            color: #FFD700;
          }
          
          .dashboard-wrapper {
            min-height: 100vh;
            background-color: #f8f9fa;
          }
        `}
      </style>
    </div>
  );
};

export default XGolifeDashboardComponent;